import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  Typography,
  Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import api from "../utils/api";

const LicenseForm = ({
  license,
  categories,
  existingCategoryGroups = [],
  onSubmit,
  onCancel,
}) => {
  console.log("LicenseForm rendered with props:", {
    license,
    categories,
    onSubmit,
    onCancel,
  });
  const [formData, setFormData] = useState({
    name: "",
    vendor: "",
    supplierId: null,
    categoryId: "",
    categoryGroup: "",
    licenseKey: "",
    licenseType: "subscription",
    billingFrequency: "yearly",
    billingAmount: "",
    currency: "GBP",
    includesVat: false,
    vatRate: 20.0,
    startDate: new Date(),
    expiryDate: new Date(),
    autoRenewal: false,
    renewalAmount: "",
    renewalCurrency: "GBP",
    renewalIncludesVat: false,
    userAllocation: 1,
    notes: "",
    status: "active",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [validationError, setValidationError] = useState("");
  const [gbpConversion, setGbpConversion] = useState(null);
  const [renewalGbpConversion, setRenewalGbpConversion] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [suppliersList, setSuppliersList] = useState([]);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const res = await api.get("/api/suppliers/list");
        setSuppliersList(res.data.suppliers || []);
      } catch (err) {
        console.warn("Could not load suppliers list:", err);
      }
    };
    fetchSuppliers();
  }, []);

  useEffect(() => {
    console.log("LicenseForm useEffect triggered with license:", license);
    // Clear any previous errors when form is reset
    setErrors({});
    setApiError("");
    setValidationError("");

    if (license) {
      console.log("Setting form data for existing license");
      setFormData({
        name: license.name || "",
        vendor: license.vendor || "",
        supplierId: license.supplier_id ?? null,
        categoryId: license.category_id || "",
        categoryGroup: license.category_group || "",
        licenseKey: license.license_key || "",
        licenseType: license.license_type || "subscription",
        billingFrequency: license.billing_frequency || "yearly",
        billingAmount: license.billing_amount || "",
        currency: license.currency || "GBP",
        includesVat: Boolean(license.includes_vat) || false,
        vatRate: license.vat_rate || 20.0,
        startDate: license.start_date
          ? new Date(license.start_date)
          : new Date(),
        expiryDate: license.expiry_date
          ? new Date(license.expiry_date)
          : new Date(),
        autoRenewal: Boolean(license.auto_renewal) || false,
        renewalAmount: license.renewal_amount || "",
        renewalCurrency: license.renewal_currency || "GBP",
        renewalIncludesVat: Boolean(license.renewal_includes_vat) || false,
        userAllocation: license.user_allocation || 1,
        notes: license.notes || "",
        status: license.status || "active",
      });
    }
  }, [license]);

  // Function to get exchange rates for all supported currencies
  const getExchangeRates = async () => {
    try {
      // Using a free exchange rate API to get all rates
      const response = await fetch(
        "https://api.exchangerate-api.com/v4/latest/GBP",
      );
      const data = await response.json();

      // Store rates for conversion to GBP (inverse of the API rates)
      const rates = {
        USD: 1 / data.rates.USD,
        EUR: 1 / data.rates.EUR,
        CAD: 1 / data.rates.CAD,
        GBP: 1, // Base currency
      };

      setExchangeRate(rates);
      return rates;
    } catch (error) {
      console.warn("Could not fetch exchange rates, using fallback:", error);
      // Fallback rates (you can update these manually)
      const fallbackRates = {
        USD: 0.79,
        EUR: 0.86,
        CAD: 0.58,
        GBP: 1,
      };
      setExchangeRate(fallbackRates);
      return fallbackRates;
    }
  };

  // Function to calculate GBP conversion for any currency
  const calculateGbpConversion = (amount, currency, rates) => {
    if (amount && rates && rates[currency]) {
      const converted = parseFloat(amount) * rates[currency];
      return converted.toFixed(2);
    }
    return null;
  };

  const handleChange = (field, value) => {
    // Special validation for status change to "expired" (case-insensitive)
    if (field === "status" && value && value.toLowerCase() === "expired") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expiryDate = formData.expiryDate
        ? new Date(formData.expiryDate)
        : null;

      if (expiryDate) {
        expiryDate.setHours(0, 0, 0, 0);
        if (expiryDate > today) {
          // Expiry date hasn't passed yet - show warning and prevent change
          setErrors((prev) => ({
            ...prev,
            status:
              "Cannot set status to 'Expired' - expiry date has not passed. Please update the expiry date first.",
            expiryDate:
              "Expiry date must be in the past to set status to 'Expired'",
          }));
          setValidationError(
            "Cannot set status to 'Expired' because the expiry date is in the future. Please update the expiry date first.",
          );
          return; // Prevent the status change
        }
      } else {
        // No expiry date set - require it first
        setErrors((prev) => ({
          ...prev,
          status: "Cannot set status to 'Expired' - expiry date is required",
          expiryDate: "Expiry date is required to set status to 'Expired'",
        }));
        setValidationError(
          "Please set an expiry date before setting status to 'Expired'.",
        );
        return; // Prevent the status change
      }
    }

    // Clear status error if changing to something other than expired
    if (field === "status" && value !== "expired" && errors.status) {
      setErrors((prev) => ({
        ...prev,
        status: "",
      }));
    }

    // Validate expiry date change when status is already "expired" (case-insensitive)
    if (
      field === "expiryDate" &&
      formData.status &&
      formData.status.toLowerCase() === "expired"
    ) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const newExpiryDate = value ? new Date(value) : null;

      if (newExpiryDate) {
        newExpiryDate.setHours(0, 0, 0, 0);
        if (newExpiryDate > today) {
          // Trying to set future expiry date while status is expired - prevent it
          setErrors((prev) => ({
            ...prev,
            expiryDate:
              "Cannot set expiry date to future when status is 'Expired'. Please change status first or set expiry date to past.",
            status:
              "Status is 'Expired' but expiry date is in the future. Please update expiry date to past or change status.",
          }));
          setValidationError(
            "Cannot have 'Expired' status with a future expiry date. Please update the expiry date to a past date or change the status.",
          );
          return; // Prevent the expiry date change
        } else {
          // Expiry date is in the past, clear any errors
          setErrors((prev) => ({
            ...prev,
            expiryDate: "",
            status: "",
          }));
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field] && field !== "status") {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Clear errors when user makes changes
    if (apiError) {
      setApiError("");
    }
    if (validationError && field !== "status") {
      setValidationError("");
    }

    // Handle currency change - fetch rates for any non-GBP currency
    if (field === "currency" && value !== "GBP") {
      getExchangeRates();
    }

    // Handle renewal currency change - fetch rates for any non-GBP currency
    if (field === "renewalCurrency" && value !== "GBP") {
      getExchangeRates();
    }

    // Handle billing amount change for currency conversion
    if (field === "billingAmount" && formData.currency !== "GBP" && value) {
      if (exchangeRate && exchangeRate[formData.currency]) {
        const converted = calculateGbpConversion(
          value,
          formData.currency,
          exchangeRate,
        );
        setGbpConversion(converted);
      } else {
        getExchangeRates().then((rates) => {
          const converted = calculateGbpConversion(
            value,
            formData.currency,
            rates,
          );
          setGbpConversion(converted);
        });
      }
    } else if (field === "billingAmount" && formData.currency === "GBP") {
      setGbpConversion(null);
    }

    // Handle renewal amount change for currency conversion
    if (
      field === "renewalAmount" &&
      formData.renewalCurrency !== "GBP" &&
      value
    ) {
      if (exchangeRate && exchangeRate[formData.renewalCurrency]) {
        const converted = calculateGbpConversion(
          value,
          formData.renewalCurrency,
          exchangeRate,
        );
        setRenewalGbpConversion(converted);
      } else {
        getExchangeRates().then((rates) => {
          const converted = calculateGbpConversion(
            value,
            formData.renewalCurrency,
            rates,
          );
          setRenewalGbpConversion(converted);
        });
      }
    } else if (
      field === "renewalAmount" &&
      formData.renewalCurrency === "GBP"
    ) {
      setRenewalGbpConversion(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "License name is required";
    }

    if (!formData.vendor.trim()) {
      newErrors.vendor = "Supplier name is required";
    }

    console.log(
      "Validating billing amount:",
      formData.billingAmount,
      "Type:",
      typeof formData.billingAmount,
    );
    if (!formData.billingAmount || parseFloat(formData.billingAmount) < 0) {
      newErrors.billingAmount = "Billing amount cannot be negative";
      console.log("Billing amount validation failed:", formData.billingAmount);
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!formData.expiryDate) {
      newErrors.expiryDate = "Expiry date is required";
    }

    if (!formData.userAllocation || parseInt(formData.userAllocation) < 1) {
      newErrors.userAllocation = "User allocation must be at least 1";
    }

    if (
      formData.startDate &&
      formData.expiryDate &&
      formData.expiryDate <= formData.startDate
    ) {
      newErrors.expiryDate = "Expiry date must be after start date";
    }

    if (
      formData.autoRenewal &&
      (!formData.renewalAmount || parseFloat(formData.renewalAmount) < 0)
    ) {
      newErrors.renewalAmount =
        "Renewal amount cannot be negative when auto-renewal is enabled";
    }

    // Validate status = expired requires expiry date to be in the past
    // Check case-insensitively
    if (formData.status && formData.status.toLowerCase() === "expired") {
      console.log(
        "Validating expired status - expiryDate:",
        formData.expiryDate,
        "type:",
        typeof formData.expiryDate,
      );
      if (!formData.expiryDate) {
        newErrors.status = "Expiry date is required to set status to 'Expired'";
        newErrors.expiryDate = "Expiry date is required";
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Handle both Date objects and date strings
        let expiryDate;
        if (formData.expiryDate instanceof Date) {
          expiryDate = new Date(formData.expiryDate);
        } else if (typeof formData.expiryDate === "string") {
          expiryDate = new Date(formData.expiryDate);
        } else {
          expiryDate = new Date(formData.expiryDate);
        }

        expiryDate.setHours(0, 0, 0, 0);

        console.log(
          "Date comparison - today:",
          today.toISOString(),
          "expiryDate:",
          expiryDate.toISOString(),
          "expiryDate > today:",
          expiryDate > today,
        );

        if (isNaN(expiryDate.getTime())) {
          newErrors.expiryDate = "Invalid expiry date";
          newErrors.status =
            "Invalid expiry date - cannot set status to 'Expired'";
        } else if (expiryDate > today) {
          newErrors.status =
            "Cannot set status to 'Expired' - expiry date has not passed. Please update the expiry date to a past date first.";
          newErrors.expiryDate =
            "Expiry date must be in the past to set status to 'Expired'";
        }
      }
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleSubmit = async (e) => {
    console.log("=== FORM SUBMIT START ===");
    console.log("Form submit triggered");
    console.log("Event:", e);
    e.preventDefault();

    console.log("Current form data:", formData);
    console.log("Validating form...");
    const validationResult = validateForm();
    console.log("Validation result:", validationResult);

    // Check if validation failed
    if (!validationResult.isValid) {
      console.log("Form validation failed - preventing submission");
      // Use the errors directly from validateForm
      const errorMessages = Object.entries(validationResult.errors)
        .filter(([_, msg]) => msg)
        .map(([field, msg]) => {
          // Make field names more readable
          const fieldNames = {
            status: "Status",
            expiryDate: "Expiry Date",
            name: "Name",
            vendor: "Supplier Name",
            billingAmount: "Billing Amount",
            startDate: "Start Date",
            userAllocation: "User Allocation",
            renewalAmount: "Renewal Amount",
          };
          return `${fieldNames[field] || field}: ${msg}`;
        });

      if (errorMessages.length > 0) {
        setValidationError(
          `Please fix the following errors: ${errorMessages.join("; ")}`,
        );
      } else {
        setValidationError(
          "Please fix the validation errors before submitting.",
        );
      }
      setLoading(false);
      return;
    }

    // Double-check validation before submitting (case-insensitive)
    console.log(
      "Form validation passed, double-checking status/expiry date...",
    );
    console.log("Status:", formData.status, "ExpiryDate:", formData.expiryDate);
    if (
      formData.status &&
      formData.status.toLowerCase() === "expired" &&
      formData.expiryDate
    ) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let expiryDate;
      if (formData.expiryDate instanceof Date) {
        expiryDate = new Date(formData.expiryDate);
      } else {
        expiryDate = new Date(formData.expiryDate);
      }
      expiryDate.setHours(0, 0, 0, 0);

      console.log(
        "Double-check - today:",
        today.toISOString(),
        "expiryDate:",
        expiryDate.toISOString(),
        "isFuture:",
        expiryDate > today,
      );

      if (!isNaN(expiryDate.getTime()) && expiryDate > today) {
        console.log(
          "BLOCKING SUBMISSION: Expiry date is in the future but status is expired",
        );
        setValidationError(
          "Cannot save: Status is 'Expired' but expiry date is in the future. Please update the expiry date to a past date or change the status.",
        );
        setErrors({
          status: "Cannot set status to 'Expired' - expiry date has not passed",
          expiryDate:
            "Expiry date must be in the past to set status to 'Expired'",
        });
        setLoading(false);
        return;
      }
    }

    console.log("Form validation passed, setting loading...");
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        startDate:
          formData.startDate instanceof Date
            ? formData.startDate.toISOString().split("T")[0]
            : formData.startDate,
        expiryDate:
          formData.expiryDate instanceof Date
            ? formData.expiryDate.toISOString().split("T")[0]
            : formData.expiryDate,
      };

      console.log("Submit data prepared:", submitData);

      // Final safety check - prevent submission if status is expired with future date
      if (
        submitData.status &&
        submitData.status.toLowerCase() === "expired" &&
        submitData.expiryDate
      ) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiryDate = new Date(submitData.expiryDate);
        expiryDate.setHours(0, 0, 0, 0);

        console.log(
          "Final safety check - today:",
          today.toISOString(),
          "expiryDate:",
          expiryDate.toISOString(),
          "isFuture:",
          expiryDate > today,
        );

        if (!isNaN(expiryDate.getTime()) && expiryDate > today) {
          console.log("BLOCKING SUBMISSION AT FINAL CHECK");
          setLoading(false);
          setValidationError(
            "Cannot save: Status is 'Expired' but expiry date is in the future. Please update the expiry date to a past date or change the status.",
          );
          setErrors({
            status:
              "Cannot set status to 'Expired' - expiry date has not passed",
            expiryDate:
              "Expiry date must be in the past to set status to 'Expired'",
          });
          return;
        }
      }

      // Remove renewal fields if autoRenewal is false
      if (!formData.autoRenewal) {
        console.log("AutoRenewal is false, removing renewal fields");
        delete submitData.renewalAmount;
        delete submitData.renewalCurrency;
        delete submitData.renewalIncludesVat;
      }

      console.log("Calling onSubmit with data:", submitData);
      await onSubmit(submitData);
      console.log("onSubmit completed successfully");
    } catch (error) {
      console.error("Form submission error:", error);

      // Handle API validation errors
      if (error.response?.data?.errors) {
        console.log("Backend validation errors:", error.response.data.errors);
        const validationErrors = {};
        error.response.data.errors.forEach((err) => {
          console.log("Processing backend error:", err);
          // Map backend field names to frontend field names
          const fieldMap = {
            name: "name",
            vendor: "vendor",
            licenseType: "licenseType",
            billingFrequency: "billingFrequency",
            billingAmount: "billingAmount",
            startDate: "startDate",
            expiryDate: "expiryDate",
            categoryId: "categoryId",
            licenseKey: "licenseKey",
            currency: "currency",
            includesVat: "includesVat",
            vatRate: "vatRate",
            autoRenewal: "autoRenewal",
            renewalAmount: "renewalAmount",
            userAllocation: "userAllocation",
            notes: "notes",
          };

          const frontendField = fieldMap[err.field] || err.field;
          validationErrors[frontendField] = err.message;
          console.log("Mapped error:", {
            backendField: err.field,
            frontendField,
            message: err.message,
          });
        });

        setErrors(validationErrors);
        setApiError("Please correct the errors below and try again.");
      } else if (error.response?.data?.message) {
        setApiError(error.response.data.message);
      } else if (error.response?.data?.error) {
        setApiError(error.response.data.error);
      } else {
        setApiError("An unexpected error occurred. Please try again.");
      }

      // Re-throw the error so the parent component can handle it
      throw error;
    } finally {
      console.log("Setting loading to false");
      setLoading(false);
    }
  };

  return (
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        {(apiError || validationError) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {apiError || validationError}
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="License Name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              freeSolo
              options={suppliersList}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : option.supplierCompany || ""
              }
              value={
                formData.supplierId && suppliersList.length
                  ? suppliersList.find((s) => s.id === formData.supplierId) ||
                    formData.vendor
                  : formData.vendor || null
              }
              onChange={(_, newValue) => {
                if (typeof newValue === "object" && newValue?.id != null) {
                  handleChange("vendor", newValue.supplierCompany);
                  handleChange("supplierId", newValue.id);
                } else {
                  handleChange(
                    "vendor",
                    typeof newValue === "string" ? newValue : "",
                  );
                  handleChange("supplierId", null);
                }
              }}
              onInputChange={(_, inputValue) => {
                if (inputValue !== (formData.vendor || "")) {
                  handleChange("vendor", inputValue);
                  handleChange("supplierId", null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supplier Name"
                  placeholder="Select from list or type manually"
                  error={!!errors.vendor}
                  helperText={errors.vendor}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.categoryId}
                label="Category"
                onChange={(e) => handleChange("categoryId", e.target.value)}
              >
                <MenuItem value="">No Category</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              freeSolo
              options={existingCategoryGroups}
              value={formData.categoryGroup || null}
              onChange={(event, newValue) => {
                handleChange("categoryGroup", newValue || "");
              }}
              onInputChange={(event, newInputValue) => {
                if (event && event.type === "change") {
                  handleChange("categoryGroup", newInputValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category Group"
                  placeholder="Select or type to create new"
                  helperText="High-level classification (e.g., Employee Benefits, Licensing, Subscriptions)"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="License Key"
              value={formData.licenseKey}
              onChange={(e) => handleChange("licenseKey", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>License Type</InputLabel>
              <Select
                value={formData.licenseType}
                label="License Type"
                onChange={(e) => handleChange("licenseType", e.target.value)}
              >
                <MenuItem value="perpetual">Perpetual</MenuItem>
                <MenuItem value="subscription">Subscription</MenuItem>
                <MenuItem value="trial">Trial</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Billing Frequency</InputLabel>
              <Select
                value={formData.billingFrequency}
                label="Billing Frequency"
                onChange={(e) =>
                  handleChange("billingFrequency", e.target.value)
                }
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
                <MenuItem value="one-time">One-time</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Billing Amount"
              type="number"
              value={formData.billingAmount}
              onChange={(e) => handleChange("billingAmount", e.target.value)}
              error={!!errors.billingAmount}
              helperText={errors.billingAmount}
              required
            />
            {formData.currency !== "GBP" && gbpConversion && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                >
                  GBP Equivalent: £{gbpConversion}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Exchange Rate:{" "}
                  {exchangeRate && exchangeRate[formData.currency]
                    ? exchangeRate[formData.currency].toFixed(4)
                    : "N/A"}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.includesVat)}
                  onChange={(e) =>
                    handleChange("includesVat", e.target.checked)
                  }
                />
              }
              label="Amount includes VAT"
            />
          </Grid>

          {formData.includesVat && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="VAT Rate (%)"
                type="number"
                value={formData.vatRate}
                onChange={(e) => handleChange("vatRate", e.target.value)}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                helperText="Default: 20%"
              />
            </Grid>
          )}

          {formData.includesVat && formData.billingAmount && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Net Amount (excl. VAT)"
                  type="number"
                  value={(
                    (parseFloat(formData.billingAmount) || 0) /
                    (1 + (parseFloat(formData.vatRate) || 20) / 100)
                  ).toFixed(2)}
                  InputProps={{ readOnly: true }}
                  helperText="Calculated automatically"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="VAT Amount"
                  type="number"
                  value={(
                    (parseFloat(formData.billingAmount) || 0) -
                    (parseFloat(formData.billingAmount) || 0) /
                      (1 + (parseFloat(formData.vatRate) || 20) / 100)
                  ).toFixed(2)}
                  InputProps={{ readOnly: true }}
                  helperText="Calculated automatically"
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                label="Currency"
                onChange={(e) => handleChange("currency", e.target.value)}
              >
                <MenuItem value="GBP">GBP</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="CAD">CAD</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Start Date"
              value={formData.startDate}
              onChange={(date) => handleChange("startDate", date)}
              textField={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Expiry Date"
              value={formData.expiryDate}
              onChange={(date) => handleChange("expiryDate", date)}
              textField={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.expiryDate}
                  helperText={errors.expiryDate}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.status}>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleChange("status", e.target.value)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
              {errors.status && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {errors.status}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="User Allocation"
              type="number"
              value={formData.userAllocation}
              onChange={(e) => handleChange("userAllocation", e.target.value)}
              error={!!errors.userAllocation}
              helperText={
                errors.userAllocation ||
                "Number of users this license is allocated to"
              }
              required
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.autoRenewal)}
                  onChange={(e) =>
                    handleChange("autoRenewal", e.target.checked)
                  }
                />
              }
              label="Auto Renewal"
            />
          </Grid>

          {formData.autoRenewal && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Renewal Amount"
                  type="number"
                  value={formData.renewalAmount}
                  onChange={(e) =>
                    handleChange("renewalAmount", e.target.value)
                  }
                  error={!!errors.renewalAmount}
                  helperText={errors.renewalAmount}
                />
                {formData.renewalCurrency !== "GBP" && renewalGbpConversion && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                    >
                      GBP Equivalent: £{renewalGbpConversion}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      Exchange Rate:{" "}
                      {exchangeRate && exchangeRate[formData.renewalCurrency]
                        ? exchangeRate[formData.renewalCurrency].toFixed(4)
                        : "N/A"}
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Renewal Currency</InputLabel>
                  <Select
                    value={formData.renewalCurrency}
                    label="Renewal Currency"
                    onChange={(e) =>
                      handleChange("renewalCurrency", e.target.value)
                    }
                  >
                    <MenuItem value="GBP">GBP</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="CAD">CAD</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.renewalIncludesVat)}
                      onChange={(e) =>
                        handleChange("renewalIncludesVat", e.target.checked)
                      }
                    />
                  }
                  label="Renewal amount includes VAT"
                />
              </Grid>

              {formData.renewalIncludesVat && formData.renewalAmount && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Renewal Net Amount (excl. VAT)"
                      type="number"
                      value={(
                        (parseFloat(formData.renewalAmount) || 0) /
                        (1 + (parseFloat(formData.vatRate) || 20) / 100)
                      ).toFixed(2)}
                      InputProps={{ readOnly: true }}
                      helperText="Calculated automatically"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Renewal VAT Amount"
                      type="number"
                      value={(
                        (parseFloat(formData.renewalAmount) || 0) -
                        (parseFloat(formData.renewalAmount) || 0) /
                          (1 + (parseFloat(formData.vatRate) || 20) / 100)
                      ).toFixed(2)}
                      InputProps={{ readOnly: true }}
                      helperText="Calculated automatically"
                    />
                  </Grid>
                </>
              )}
            </>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button onClick={onCancel} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                onClick={() => {
                  console.log("Submit button clicked - onClick handler");
                }}
              >
                {license ? "Update License" : "Create License"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
  );
};

export default LicenseForm;
